import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Users, Calendar, AlertCircle, Search, X, User } from 'lucide-react';

export const PatientsPanel = ({ MAX_PATIENT_RESULTS, filteredRealPatients, handlePatientClick, patientSearchTerm, realPatients, selectedPatientId, setPatientSearchTerm, shownPatients }) => (
  <>
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center text-2xl">
            <Users className="h-6 w-6 mr-2 text-blue-600" />
            Patients
            {patientSearchTerm && (
              <Badge variant="outline" className="ml-2">
                Search: "{patientSearchTerm}"
              </Badge>
            )}
          </CardTitle>
          {patientSearchTerm && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPatientSearchTerm('')}
              className="text-xs"
            >
              <X className="h-3 w-3 mr-1" />
              Clear Search
            </Button>
          )}
        </div>

        {/* Search Box — visually prominent + instructive */}
        <div className="mt-4 space-y-2">
          <Label
            htmlFor="patient-search-input"
            className="flex items-center gap-2 text-lg font-bold"
            style={{ color: '#0586ba' }}
          >
            <Search className="h-5 w-5" style={{ color: '#0586ba' }} />
            Find a Patient
          </Label>
          <p className="text-base font-bold text-gray-700 -mt-1">
            Type any part of a name or email below, then click the patient row to open their record.
          </p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none" style={{ color: '#0586ba' }} />
            <Input
              id="patient-search-input"
              type="text"
              placeholder="Start typing a patient's name or email…"
              value={patientSearchTerm}
              onChange={(e) => setPatientSearchTerm(e.target.value)}
              className="pl-10 pr-10 h-14 text-lg font-bold bg-white placeholder:text-gray-500 placeholder:font-bold placeholder:text-lg shadow-sm focus:ring-2"
              style={{
                border: '4px solid #0586ba',
                outlineColor: '#0586ba',
              }}
              data-testid="patient-search-input"
              autoComplete="off"
            />
            {patientSearchTerm && (
              <button
                type="button"
                onClick={() => setPatientSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700"
                aria-label="Clear search"
                data-testid="patient-search-clear-inline"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filter Info */}
        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-gray-500">
            {patientSearchTerm ? (
              <>
                <strong className="text-blue-700">{filteredRealPatients.length}</strong> match{filteredRealPatients.length === 1 ? '' : 'es'} for <em>"{patientSearchTerm}"</em>
                {filteredRealPatients.length > MAX_PATIENT_RESULTS && <span className="text-gray-400"> — showing the first {MAX_PATIENT_RESULTS}, keep typing to narrow</span>}
              </>
            ) : (
              <>
                <strong className="text-gray-700">{realPatients.length}</strong> patients on file — <span className="text-gray-400">type a name or email above to find one</span>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {shownPatients.length > 0 ? (
          <div className="space-y-4" data-testid="patient-search-results">
            {shownPatients.map((patient) => (
              <div 
                key={patient.id} 
                onClick={() => handlePatientClick(patient.id)}
                className={`flex justify-between items-center p-4 rounded-lg cursor-pointer transition-colors border ${
                  selectedPatientId === patient.id 
                    ? 'bg-blue-100 border-2 border-blue-300' 
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-xl">{patient.firstName} {patient.lastName}</h4>
                    <Badge 
                      variant={patient.status === 'Active' ? 'default' : 'secondary'}
                      className={`text-sm ${
                        patient.status === 'Active' 
                          ? 'bg-green-100 text-green-800 border-green-200' 
                          : 'bg-gray-100 text-gray-600 border-gray-200'
                      }`}
                    >
                      {patient.status || 'Active'}
                    </Badge>
                    {/* Real Patient badge removed - all patients are real */}
                  </div>

                  <div className="space-y-1 text-base text-gray-600">
                    <div className="flex items-center gap-1">
                      <User className="h-5 w-5" />
                      <span>{patient.email}</span>
                    </div>

                    {patient.lastLoginAt && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-5 w-5" />
                        <span>Last login: {new Date(patient.lastLoginAt).toLocaleDateString()}</span>
                      </div>
                    )}

                    {patient.status === 'Inactive' && patient.deactivatedAt && (
                      <div className="flex items-center gap-1 text-red-600">
                        <AlertCircle className="h-5 w-5" />
                        <span>Deactivated: {new Date(patient.deactivatedAt).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center">
                  <div className={`flex items-center ${selectedPatientId === patient.id ? 'text-blue-600' : 'text-gray-400'}`}>
                    <Users className="h-5 w-5" />
                    {selectedPatientId === patient.id && (
                      <span className="ml-2 text-xs font-medium">Selected</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500" data-testid="patient-search-empty">
            <Search className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p className="text-base">
              {patientSearchTerm
                ? `No patients match "${patientSearchTerm}"`
                : 'Start typing above to find a patient'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  </>
);
